import { useTranslation, t } from '../../../../../shared/i18n';
import React from 'react';
import { Button, Checkbox, Tag, Tooltip, Skeleton } from 'antd';
import { CheckOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { formatMoney } from '../../../../../shared/utils/money';
import { resultState, resultKey, friendlyError } from './searchUtils';
import { DomainName, PriceBlock, discountPct } from './Showcase';

const getStateTags = () => ({
    available: <Tag color="success">{t('Available to register')}</Tag>,
    premium: (
        <Tooltip title={t('Premium domain priced by the registry')}>
            <Tag color="gold">{t('Premium')}</Tag>
        </Tooltip>
    ),
    taken: <Tag>{t('Already registered')}</Tag>,
    unsupported: <Tag color="warning">{t('This language is not supported')}</Tag>,
});

/**
 * 结果行价格：首年 + 续费（续费更贵时标橙）
 */
export const PriceCell = ({ register, renew, currency, listPrice, promo }) => {
    const { t } = useTranslation();
    if (register == null || register === '') return <span className="ds-renew">-</span>;
    const reg = Number(register);
    const ren = renew == null || renew === '' ? null : Number(renew);
    const list = listPrice != null && Number(listPrice) > reg + 0.001 ? Number(listPrice) : null;
    return (
        <>
            <div className="ds-first">
                {promo && <span style={{ color: '#cf1322', fontSize: 12, marginRight: 4 }}>{t('Special offer')}</span>}
                {formatMoney(reg, currency)}
                {list != null && <span className="ds-renew" style={{ textDecoration: 'line-through', marginLeft: 4 }}>{formatMoney(list, currency)}</span>}
                <span className="ds-renew"> {t('First year')}</span>
            </div>
            {ren != null && (
                <div className={`ds-renew ${ren > reg + 0.001 ? 'is-higher' : ''}`}>
                    {t('Renew for {{price}}/year', { price: formatMoney(ren, currency) })}
                </div>
            )}
        </>
    );
};

/**
 * 搜索结果列表
 * props:
 *  rows            结果数组（后端 check-availability 记录）
 *  pendingKeys     仍在查询中的域名（显示骨架）
 *  exactKey        精确匹配的域名（高亮）
 *  inCart          Set<domain>
 *  adding          Set<domain>
 *  onAdd(record)   加入购物车
 *  onTransfer(record)
 *  selectable / selected / onSelect(keys)
 *  actionLabel     optional: a labelled primary button instead of the cart icon (e.g. "Buy" in the site builder)
 */
const ResultList = ({
    rows = [],
    pendingKeys = [],
    exactKey,
    inCart,
    adding,
    onAdd,
    onTransfer,
    selectable = false,
    selected = [],
    onSelect,
    tldInfo,
    actionLabel,
}) => {
    const { t } = useTranslation();
    const selSet = new Set(selected);
    const toggle = (key, on) => {
        if (!onSelect) return;
        const next = new Set(selSet);
        if (on) next.add(key); else next.delete(key);
        onSelect([...next]);
    };

    if (!rows.length && !pendingKeys.length) return null;

    return (
        <div className={`ds-list ${selectable ? '' : 'no-select'}`}>
            {rows.map((r) => {
                const key = resultKey(r);
                const state = resultState(r);
                const canBuy = state === 'available' || state === 'premium';
                const added = inCart?.has(key);
                return (
                    <div key={key} className={`ds-row ${key === exactKey ? 'is-exact' : ''} ${canBuy ? '' : 'is-muted'}`} data-domain={key}>
                        {selectable && (
                            <div className="ds-sel">
                                <Checkbox disabled={!canBuy || added} checked={selSet.has(key)} onChange={(e) => toggle(key, e.target.checked)} />
                            </div>
                        )}
                        <div className="ds-dom">
                            <div className="ds-domain">
                                <DomainName r={r} tldUnicode={tldInfo?.(r.tld)?.tld_unicode} />
                                {r.pricing?.promo && canBuy && discountPct(r.pricing.register, r.pricing.list_price) > 0 && (
                                    <span className="ds-off inline">-{discountPct(r.pricing.register, r.pricing.list_price)}%</span>
                                )}
                                {canBuy && tldInfo?.(r.tld)?.is_featured && <span className="ds-badge">{t('Popular')}</span>}
                                {r.domain_unicode && r.domain_unicode !== r.domain && /^[\w.-]+$/.test(r.domain) && <small>{r.domain}</small>}
                            </div>
                        </div>
                        <div className="ds-state">
                            {state === 'available' ? null : (getStateTags()[state] || (
                                <Tooltip title={friendlyError(r)}>
                                    <Tag>{t('Currently unavailable for registration')}</Tag>
                                </Tooltip>
                            ))}
                        </div>
                        <div className="ds-price">
                            {canBuy ? <PriceBlock r={r} /> : <span className="ds-renew">-</span>}
                        </div>
                        <div className="ds-act">
                            {canBuy && (added ? (
                                <Tooltip title={t('Already in cart')}>
                                    <Button className="ds-cartbtn is-in" icon={<CheckOutlined />} disabled />
                                </Tooltip>
                            ) : (
actionLabel ? (
                                <Button type="primary" loading={adding?.has(key)} onClick={() => onAdd?.(r)}>{actionLabel}</Button>
                            ) : (
                                <Tooltip title={t('Add to cart')}>
                                    <Button className="ds-cartbtn" icon={<ShoppingCartOutlined />} loading={adding?.has(key)} onClick={() => onAdd?.(r)} aria-label={t('Add to cart')} />
                                </Tooltip>
                            )
                            ))}
                            {state === 'taken' && onTransfer && (
                                <Tooltip title={t('Already own this domain? Transfer it to Yeap')}>
                                    <Button type="link" onClick={() => onTransfer(r)}>{t('Transfer in')}</Button>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                );
            })}
            {pendingKeys.map((k) => (
                <div key={`p-${k}`} className={`ds-row ${selectable ? '' : ''}`}>
                    {selectable && <div className="ds-sel" />}
                    <div className="ds-dom"><div className="ds-domain is-muted" style={{ color: 'var(--text-3)' }}>{k}</div></div>
                    <div className="ds-state"><Skeleton.Button active size="small" style={{ width: 60 }} /></div>
                    <div className="ds-price"><Skeleton.Input active size="small" style={{ width: 90, minWidth: 0 }} /></div>
                    <div className="ds-act" />
                </div>
            ))}
        </div>
    );
};

export default ResultList;
